<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('');
const selectedTeamIds = ref([]);
const agentCpf = ref('');
const agentDataNascimento = ref('');
const agentSetor = ref('');
const agentRamal = ref('');
const agentTelefone = ref('');
const agentAccessibilityProfile = ref('default');
const nameInput = ref(null);

const isValidCpf = value => {
  const cpf = String(value || '').replace(/\D/g, '');
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const digit = length => {
    const sum = cpf
      .slice(0, length)
      .split('')
      .reduce(
        (total, number, index) => total + Number(number) * (length + 1 - index),
        0
      );
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };
  return digit(9) === Number(cpf[9]) && digit(10) === Number(cpf[10]);
};

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  agentCpf: {
    required,
    validCpf: helpers.withMessage(
      t('AGENT_MGMT.PROFILE_FIELDS.CPF_INVALID'),
      isValidCpf
    ),
  },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
  agentCpf,
  agentDataNascimento,
  agentSetor,
  agentRamal,
  agentTelefone,
  selectedTeamIds,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const teamsList = computed(() => store.getters['teams/getTeams'] || []);
const today = new Date().toISOString().slice(0, 10);

onMounted(async () => {
  store.dispatch('teams/get');
  await nextTick();
  nameInput.value?.focus();
});
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const isAdministrator = computed(
  () => currentAccount.value.role === 'administrator'
);

const roles = computed(() => {
  const defaultRoles = isAdministrator.value
    ? [
        {
          id: 'administrator',
          name: 'administrator',
          label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
        },
      ]
    : [];

  const managerPermissions = currentAccount.value.permissions || [];
  const customRoles = getCustomRoles.value
    .filter(
      role =>
        isAdministrator.value ||
        role.permissions.every(permission =>
          managerPermissions.includes(permission)
        )
    )
    .map(role => ({
      id: role.id,
      name: `custom_${role.id}`,
      label: role.name,
    }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
      custom_attributes: {
        cpf: agentCpf.value,
        data_nascimento: agentDataNascimento.value,
        setor: agentSetor.value,
        ramal: agentRamal.value,
        telefone: agentTelefone.value,
        accessibility_profile: agentAccessibilityProfile.value,
      },
      team_ids: selectedTeamIds.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};

const applyCpfMask = value => {
  return value
    .replace(/\D/g, '') // Remove tudo que não é dígito
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1'); // Limita a 11 dígitos
};

const handleCpfInput = e => {
  agentCpf.value = applyCpfMask(e.target.value);
  v$.value.agentCpf.$touch();
};

const applyPhoneMask = value => {
  let r = value.replace(/\D/g, '');
  if (r.length > 11) r = r.slice(0, 11);

  if (r.length === 0) return '';
  if (r.length <= 2) return `(${r}`;
  if (r.length <= 6) return `(${r.slice(0, 2)}) ${r.slice(2)}`;
  if (r.length <= 10)
    return `(${r.slice(0, 2)}) ${r.slice(2, 6)}-${r.slice(6)}`;
  return `(${r.slice(0, 2)}) ${r.slice(2, 7)}-${r.slice(7)}`;
};

const handlePhoneInput = e => {
  agentTelefone.value = applyPhoneMask(e.target.value);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option disabled value="">
              {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.PLACEHOLDER') }}
            </option>
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentCpf.$error }">
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.CPF') }} *
          <input
            v-model="agentCpf"
            type="text"
            :placeholder="$t('AGENT_MGMT.PROFILE_FIELDS.CPF_PLACEHOLDER')"
            inputmode="numeric"
            autocomplete="off"
            maxlength="14"
            @input="handleCpfInput"
          />
          <span v-if="v$.agentCpf.$error" class="message">
            {{ v$.agentCpf.$errors[0]?.$message }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label>
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.BIRTH_DATE') }}
          <input v-model="agentDataNascimento" type="date" :max="today" />
        </label>
      </div>

      <div class="w-full">
        <label>
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.DEPARTMENT') }}
          <input
            v-model="agentSetor"
            type="text"
            :placeholder="
              $t('AGENT_MGMT.PROFILE_FIELDS.DEPARTMENT_PLACEHOLDER')
            "
          />
        </label>
      </div>

      <fieldset class="p-4 mt-2 w-full rounded-lg border border-n-weak">
        <legend class="px-2 text-sm font-semibold text-n-slate-12">
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.TEAMS') }}
        </legend>
        <p v-if="!teamsList.length" class="mb-0 text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.NO_TEAMS') }}
        </p>
        <label
          v-for="team in teamsList"
          :key="team.id"
          class="flex items-center gap-2 py-1 mb-0"
        >
          <input v-model="selectedTeamIds" type="checkbox" :value="team.id" />
          <span>{{ team.name }}</span>
        </label>
      </fieldset>

      <div class="w-full flex gap-4">
        <label class="w-1/2">
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.EXTENSION') }}
          <input
            v-model="agentRamal"
            type="text"
            :placeholder="$t('AGENT_MGMT.PROFILE_FIELDS.EXTENSION_PLACEHOLDER')"
          />
        </label>
        <label class="w-1/2">
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.PHONE') }}
          <input
            v-model="agentTelefone"
            type="text"
            :placeholder="$t('AGENT_MGMT.PROFILE_FIELDS.PHONE_PLACEHOLDER')"
            @input="handlePhoneInput"
          />
        </label>
      </div>

      <fieldset class="p-4 mt-2 w-full rounded-lg border border-n-weak">
        <legend class="px-2 text-sm font-semibold text-n-slate-12">
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.ACCESSIBILITY') }}
        </legend>
        <label for="agent-accessibility-profile">
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.INITIAL_ACCESSIBILITY_PROFILE') }}
          <select
            id="agent-accessibility-profile"
            v-model="agentAccessibilityProfile"
          >
            <option value="default">
              {{
                $t('AGENT_MGMT.PROFILE_FIELDS.ACCESSIBILITY_OPTIONS.DEFAULT')
              }}
            </option>
            <option value="lowVision">
              {{
                $t('AGENT_MGMT.PROFILE_FIELDS.ACCESSIBILITY_OPTIONS.LOW_VISION')
              }}
            </option>
            <option value="nonVisual">
              {{
                $t('AGENT_MGMT.PROFILE_FIELDS.ACCESSIBILITY_OPTIONS.NON_VISUAL')
              }}
            </option>
            <option value="keyboard">
              {{
                $t('AGENT_MGMT.PROFILE_FIELDS.ACCESSIBILITY_OPTIONS.KEYBOARD')
              }}
            </option>
          </select>
        </label>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('AGENT_MGMT.PROFILE_FIELDS.ACCESSIBILITY_HELP') }}
        </p>
      </fieldset>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
